import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import { useSelector } from "react-redux";

import { Col, DatePicker, Input, notification, Row, Select, Spin } from "antd";
import ButtonStandard from "../../../components/utils/button";
import { Container, Footer } from "../form/styles";

import useAnimalHandlingAlertContext from "../../../hooks/useAnimalHandlingAlertContext";
import { save } from "../../../services/animalHandlingAlertService";
import { logEventInfo } from "../../../utils/logger";

const { Option } = Select;

const EDIT_INITIAL_STATE = {
  id: null,
  handlingNumber: null,
  message: null,
  visualizationType: "CONFIRM",
  expirationDate: null,
  status: "A",
};

const validationSchema = Yup.object({
  message: Yup.string().trim().required(),
  visualizationType: Yup.string()
    .oneOf(["PERMANENT", "CONFIRM", "EXPIRATION"])
    .required(),
  expirationDate: Yup.mixed().when("visualizationType", {
    is: "EXPIRATION",
    then: Yup.mixed().required(),
    otherwise: Yup.mixed().nullable(),
  }),
});

const AnimalHandlingAlertDrawerEdit = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { editData, isEditDrawerVisible, closeEditDrawer, fetchData } =
    useAnimalHandlingAlertContext();

  const formikRef = useRef();
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const handleClose = useCallback(() => {
    if (formikRef.current) {
      formikRef.current.resetForm({ values: { ...EDIT_INITIAL_STATE } });
    }
    closeEditDrawer();
  }, [closeEditDrawer]);

  const handleSubmit = useCallback(
    async (values) => {
      setIsLoadingRequest(true);
      try {
        const body = {
          message: values.message,
          visualizationType: values.visualizationType,
          expirationDate:
            values.visualizationType === "EXPIRATION"
              ? moment(values.expirationDate).format("YYYY-MM-DD")
              : null,
          status: values.status || "A",
        };
        await save({ groupId, farmId, id: values.id, body });
        notification.success({
          message:
            translation.animalHandlingAlert.form.notifications.saveSuccess,
        });
        fetchData();
        handleClose();
      } catch (error) {
        notification.error({
          message: translation.animalHandlingAlert.form.notifications.saveError,
        });
      } finally {
        setIsLoadingRequest(false);
      }
    },
    [
      farmId,
      fetchData,
      groupId,
      handleClose,
      translation.animalHandlingAlert.form.notifications.saveError,
      translation.animalHandlingAlert.form.notifications.saveSuccess,
    ],
  );

  const handleVisibleChange = useCallback(
    (visible) => {
      if (visible && editData && formikRef.current) {
        logEventInfo("Opening edit drawer", { editData });
        formikRef.current.resetForm(
          editData
            ? { ...EDIT_INITIAL_STATE, ...editData }
            : { ...EDIT_INITIAL_STATE },
        );
      }
    },
    [editData],
  );

  return (
    <Container
      title={translation.animalHandlingAlert.form.titleEdit}
      width={600}
      onClose={handleClose}
      maskClosable={false}
      afterVisibleChange={handleVisibleChange}
      visible={isEditDrawerVisible}
    >
      <Spin spinning={isLoadingRequest}>
        <Formik
          ref={formikRef}
          enableReinitialize={false}
          initialValues={{ ...EDIT_INITIAL_STATE }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formikProps) => {
            const {
              values,
              errors,
              submitCount,
              setValues,
              handleSubmit: formikSubmit,
            } = formikProps;
            return (
              <form onSubmit={formikSubmit} autoComplete="off">
                <div className="drawerForm">
                  {/* Animal (read-only) */}
                  <Row type="flex" style={{ marginBottom: 13 }}>
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label>
                          {
                            translation.animalHandlingAlert.table.columns
                              .handlingNumber
                          }
                        </label>
                      </Row>
                      <Row>
                        <Input value={values.handlingNumber || ""} disabled />
                      </Row>
                    </Col>
                  </Row>

                  {/* Message */}
                  <Row type="flex" style={{ marginTop: 13 }} align="middle">
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label
                          className={
                            errors.message && submitCount > 0 ? "error" : ""
                          }
                        >
                          {translation.animalHandlingAlert.form.message} *
                        </label>
                      </Row>
                      <Row>
                        <Input.TextArea
                          value={values.message || ""}
                          autoSize={{ minRows: 3 }}
                          onChange={(e) =>
                            setValues({ ...values, message: e.target.value })
                          }
                        />
                      </Row>
                    </Col>
                  </Row>

                  {/* Visualization Type */}
                  <Row type="flex" style={{ marginTop: 13 }} align="middle">
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label
                          className={
                            errors.visualizationType && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.animalHandlingAlert.form
                              .visualizationType.label
                          }{" "}
                          *
                        </label>
                      </Row>
                      <Row>
                        <Select
                          value={values.visualizationType}
                          onChange={(value) => {
                            setValues({
                              ...values,
                              visualizationType: value,
                              expirationDate:
                                value !== "EXPIRATION"
                                  ? null
                                  : values.expirationDate,
                            });
                          }}
                        >
                          <Option value="PERMANENT">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.permanent
                            }
                          </Option>
                          <Option value="CONFIRM">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.confirm
                            }
                          </Option>
                          <Option value="EXPIRATION">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.expiration
                            }
                          </Option>
                        </Select>
                      </Row>
                    </Col>
                  </Row>

                  {/* Expiration Date — only visible when EXPIRATION */}
                  {values.visualizationType === "EXPIRATION" && (
                    <Row type="flex" style={{ marginTop: 13 }} align="middle">
                      <Col span={24}>
                        <Row className="rowLabel">
                          <label
                            className={
                              errors.expirationDate && submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {
                              translation.animalHandlingAlert.form
                                .expirationDate
                            }{" "}
                            *
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                              values.expirationDate
                                ? moment(values.expirationDate)
                                : null
                            }
                            disabledDate={(current) =>
                              current && current < moment().startOf("day")
                            }
                            onChange={(date) =>
                              setValues({
                                ...values,
                                expirationDate: date
                                  ? date.toISOString()
                                  : null,
                              })
                            }
                          />
                        </Row>
                      </Col>
                    </Row>
                  )}
                </div>

                <Footer>
                  <Row type="flex" justify="end">
                    <Col className="buttonsDiv">
                      <ButtonStandard
                        type="button"
                        buttonType="type7"
                        onClick={handleClose}
                      >
                        {translation.buttons.cancel}
                      </ButtonStandard>
                      <ButtonStandard type="submit" buttonType="type6">
                        {translation.buttons.save}
                      </ButtonStandard>
                    </Col>
                  </Row>
                </Footer>
              </form>
            );
          }}
        </Formik>
      </Spin>
    </Container>
  );
};

export default AnimalHandlingAlertDrawerEdit;
